import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { api, errMsg, num, rp, dt } from "@/lib/api";
import { StatusBadge, MoneyOnly } from "@/components/common";
import { PhotoUpload } from "@/components/PhotoUpload";

export default function ReceiveTask() {
  const { type, id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [qtys, setQtys] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const path = type === "purchase" ? "purchases" : "transfers";
  const load = useCallback(() => api.get(`/${path}/${id}`).then(({ data }) => {
    const d = data[type];
    setDoc(d);
    setQtys(d.lines.map((l) => l.qty));
  }), [path, id, type]);
  useEffect(() => { load(); }, [load]);
  if (!doc) return null;
  const receiving = doc.status === "waiting_receiving";
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/${path}/${id}/receive`, { lines: qtys.map(Number), photos: photos.map((p) => p.id), note });
      toast.success("Barang diterima · stok langsung masuk");
      nav(-1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setQ = (i, v) => setQtys(qtys.map((q, j) => (j === i ? Math.max(0, v) : q)));
  const from = type === "purchase" ? doc.supplier_name : doc.source_name;
  return (
    <div className="max-w-xl mx-auto pb-28">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-3"><ArrowLeft size={15} /> Kembali</button>
      <div className="bg-white border rounded-lg p-4 fade-up">
        <div className="flex items-center justify-between gap-2"><span className="num font-bold text-lg" data-testid="receive-code">{doc.code}</span><StatusBadge s={doc.status} testid="receive-status" /></div>
        <div className="text-sm text-slate-600 mt-1">Dari <b>{from}</b> → <b>{doc.destination_name}</b></div>
        {doc.sender && <div className="text-sm text-slate-600">Pengirim: <b>{doc.sender.name}</b> · dikirim {dt(doc.dispatched_at)}</div>}
        <MoneyOnly><div className="text-xs text-slate-500 mt-1">Nilai {rp(doc.total ?? doc.total_value)}</div></MoneyOnly>
      </div>

      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mt-5 mb-2">{receiving ? "Konfirmasi jumlah diterima" : "Dikirim/dibeli vs diterima"}</h2>
      <div className="space-y-2">
        {doc.lines.map((l, i) => (
          <div key={i} className="bg-white border rounded-lg p-3" data-testid={`receive-line-${i}`}>
            <div className="font-semibold text-[15px]">{l.item_name}</div>
            <div className="text-xs text-slate-500">{type === "purchase" ? "Dibeli" : "Dikirim"}: <span className="num font-semibold text-slate-800">{num(l.qty)} {l.unit}</span></div>
            {receiving ? (
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => setQ(i, Number(qtys[i]) - 1)} className="w-12 h-12 rounded-lg border grid place-items-center active:bg-slate-100" data-testid={`receive-line-${i}-minus`}><Minus /></button>
                <input type="number" step="any" inputMode="decimal" value={qtys[i]} onChange={(e) => setQ(i, e.target.value)} className="inp !h-12 text-center text-lg num font-bold flex-1" data-testid={`receive-line-${i}-qty`} />
                <button onClick={() => setQ(i, Number(qtys[i]) + 1)} className="w-12 h-12 rounded-lg border grid place-items-center active:bg-slate-100" data-testid={`receive-line-${i}-plus`}><Plus /></button>
                <span className="text-sm text-slate-500 w-12">{l.unit}</span>
              </div>
            ) : (
              <div className="flex items-center justify-between mt-1 text-sm">
                <span>Diterima: <span className={`num font-bold ${l.received_qty !== l.qty ? "text-rose-600" : "text-emerald-700"}`}>{l.received_qty == null ? "-" : `${num(l.received_qty)} ${l.unit}`}</span></span>
              </div>
            )}
            {receiving && Number(qtys[i]) !== l.qty && <div className="text-xs text-rose-600 mt-1 font-semibold">Selisih {num(Number(qtys[i]) - l.qty)} {l.unit} akan tercatat</div>}
          </div>
        ))}
      </div>

      {receiving && (
        <div className="mt-4 space-y-3">
          <PhotoUpload big value={photos} onChange={setPhotos} label="Foto barang diterima" testid="receiving-photo" />
          <textarea className="inp !h-16 py-2" placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="receive-note-input" />
        </div>
      )}
      {!receiving && <div className="mt-4 p-4 bg-white border rounded-lg text-sm text-slate-600" data-testid="receive-done-note">Transaksi ini sudah diterima & stok sudah masuk.</div>}

      {receiving && (
        <div className="fixed bottom-0 inset-x-0 lg:left-60 bg-white/95 backdrop-blur border-t p-3 z-20">
          <div className="max-w-xl mx-auto">
            <button disabled={busy} onClick={submit} data-testid="submit-receiving-button"
              className="w-full h-14 rounded-lg bg-emerald-600 text-white text-base font-bold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              Terima &amp; masukkan stok
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
